import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import ToolShell, { Panel, FieldLabel, Choice } from "@/components/tools/ToolShell";
import { STANDING_OBLIGATIONS, TOOL_PATHS, buildCalendar, numberLocale } from "@/config/tools";

const DAY = 86400000;

export default function ComplianceCalendar() {
  const { t, i18n } = useTranslation();
  const locale = numberLocale(i18n.language);
  const [fyeMonth, setFyeMonth] = useState(12);
  const [flags, setFlags] = useState({ vat: true, employees: false, intraEu: false });
  const items = useMemo(() => buildCalendar({ fyeMonth, flags }), [fyeMonth, flags]);
  const today = new Date(new Date().toDateString());
  const monthName = (m, style = "long") => new Intl.DateTimeFormat(locale, { month: style }).format(new Date(2026, m - 1, 1));
  const fmtDate = (d) => new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" }).format(d);

  return (
    <ToolShell id="calendar" path={TOOL_PATHS.calendar}>
      <div className="grid gap-px bg-[var(--line)] lg:grid-cols-12">
        <Panel className="lg:col-span-4 bg-[var(--ink)] border-0 space-y-10">
          <div>
            <FieldLabel htmlFor="cal-fye">{t("tools.calendar.fye")}</FieldLabel>
            <select
              id="cal-fye"
              value={fyeMonth}
              onChange={(e) => setFyeMonth(Number(e.target.value))}
              className="field cursor-pointer"
              data-testid="calendar-fye-select"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i} value={i + 1} style={{ background: "var(--ink)" }}>{monthName(i + 1)}</option>
              ))}
            </select>
          </div>
          <div className="grid gap-3">
            {Object.keys(flags).map((f) => (
              <Choice key={f} active={flags[f]} onClick={() => setFlags((x) => ({ ...x, [f]: !x[f] }))} testid={`calendar-flag-${f}`}>
                <span className="flex items-center gap-3">
                  <span className={`w-4 h-4 border flex items-center justify-center shrink-0 ${flags[f] ? "border-[var(--pewter)] bg-[var(--pewter)] text-[var(--ink)]" : "border-[var(--line-2)]"}`}>
                    {flags[f] && <Check size={11} />}
                  </span>
                  {t(`tools.calendar.flags.${f}`)}
                </span>
              </Choice>
            ))}
          </div>
        </Panel>
        <Panel className="lg:col-span-8 bg-[var(--ink-2)] border-0" data-testid="calendar-results">
          <div className="flex items-baseline justify-between mb-6">
            <div className="overline">{t("tools.calendar.upcoming")}</div>
            <span className="font-mono text-[10px] tracking-[0.2em] text-[var(--mute)]" data-testid="calendar-count">{items.length}</span>
          </div>
          <ol className="divide-y divide-[var(--line)]">
            {items.map(({ id, date }, i) => {
              const days = Math.round((date - today) / DAY);
              return (
                <li key={`${id}-${i}`} className="grid grid-cols-12 gap-4 py-5" data-testid={`calendar-item-${id}`}>
                  <div className="col-span-12 sm:col-span-3">
                    <div className="font-mono text-sm text-[var(--bone)]">{fmtDate(date)}</div>
                    <div className={`font-mono text-[10px] uppercase tracking-[0.18em] mt-1 ${days <= 30 ? "text-[var(--pewter)]" : "text-[var(--mute-2)]"}`}>
                      {days === 0 ? t("tools.calendar.today") : t("tools.calendar.inDays", { count: days })}
                    </div>
                  </div>
                  <div className="col-span-12 sm:col-span-9">
                    <div className="text-[var(--bone)]">{t(`tools.calendar.items.${id}.title`)}</div>
                    <div className="text-sm text-[var(--mute)] mt-1">{t(`tools.calendar.items.${id}.desc`)}</div>
                  </div>
                </li>
              );
            })}
          </ol>
          <div className="mt-10 pt-8 border-t border-[var(--line-2)]">
            <div className="overline mb-4">{t("tools.calendar.standingTitle")}</div>
            <ul className="space-y-3 text-sm text-[var(--bone-2)]">
              {STANDING_OBLIGATIONS.map((s) => (
                <li key={s} className="ps-4 border-s-2 border-[var(--pewter)]/60">{t(`tools.calendar.standing.${s}`)}</li>
              ))}
            </ul>
          </div>
        </Panel>
      </div>
    </ToolShell>
  );
}
